import request from "../vendors/request";
import AbstractComponent from "./abstract-component";
import { gsap } from 'gsap';

export default class ArticleList extends AbstractComponent {

    constructor(el) {
        super(el);
        this.DOM = {};
        this._ioCards = null;
        this.request = null;
        this.class = "c-articles-list"; // Adjusted class name for Article list
        this.action = "filter_articles_ajax"; // Adjusted action name for filtering Articles
        this.params = {
            service: null,
            technology: null,
            format: null,
            paged: 1
        };
        this.lastFetch = null;
        this.initDomElements();
        this.initPreselectedFilters();
        this.initEvents();
    }

    initDomElements() {
        this.DOM.column = this.$el.querySelector('.' + this.class + '__list'); // Adjusted class name for Article list
        this.DOM.noPostMsg = this.$el.querySelector('.no-post');
        this.DOM.moreBtn = this.$el.querySelector('.moreBtn');
        this.DOM.selects = this.$el.querySelectorAll('select');
        this.DOM.resetBtn = this.$el.querySelector('.reset');

        const initialCards = this.DOM.column ? Array.from(this.DOM.column.querySelectorAll('.c-article-card')) : [];
        this._observeCards(initialCards);
    }

    _ensureCardsObserver() {
        if (this._ioCards) return;

        this._ioCards = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const card = entry.target;
                this._ioCards.unobserve(card);
                if (card.__revealed) return;
                card.__revealed = true;
                this.animateNewCards([card], 0);
            });
        }, {
            root: null,
            threshold: 0.15,
            rootMargin: "0px 0px 0px 0px"
        });
    }

    _observeCards(nodes) {
        if (!nodes || !nodes.length) return;
        this._ensureCardsObserver();
        nodes.forEach((n) => {
            if (!n.__prepared) {
                gsap.set(n, { y: 40, autoAlpha: 0 });
                n.__prepared = true;
            }
            this._ioCards.observe(n);
        });
    }

    initPreselectedFilters() {
        // Vérifier les filtres présélectionnés depuis les selects
        this.DOM.selects.forEach(select => {
            const filterName = select.getAttribute('name');
            const filterValue = select.value;

            // Ignorer la première option (généralement "Tous" ou valeur par défaut)
            if (filterValue && filterValue !== '' && filterValue !== 'all') {
                this.params[filterName] = filterValue;
            }
        });

        // Afficher le bouton reset si des filtres sont présélectionnés
        const hasActiveFilters = this.params.service || this.params.technology || this.params.format;
        if (hasActiveFilters) {
            this.app.gsap.set(this.DOM.resetBtn, { display: 'flex', autoAlpha: 1, x: 0 });
        } else {
            this.app.gsap.set(this.DOM.resetBtn, { display: 'none', autoAlpha: 0, x: -5 });
        }
    }

    initEvents() {
        if (this.DOM.moreBtn) {
            this.DOM.moreBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.getNextPage();
            });
        }
        this.DOM.resetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.resetFilters()
        })
        this.DOM.selects.forEach(select => {
            select.addEventListener('change', () => {
                const filterName = select.getAttribute('name');
                const filterValue = select.value;

                this.lastFetch = 'filter'
                this.params[filterName] = filterValue;

                // Vérifier si au moins un filtre est actif
                const hasActiveFilters = this.params.service || this.params.technology || this.params.format;

                if (!hasActiveFilters) {
                    this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 1, x: 0 }, {
                        autoAlpha: 0, x: -5, onComplete: () => {
                            this.app.gsap.set(this.DOM.resetBtn, { display: 'none' })
                        }
                    })
                } else {
                    this.app.gsap.set(this.DOM.resetBtn, { display: 'flex' })
                    this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 0, x: 5 }, {
                        autoAlpha: 1, x: 0
                    })
                }
                this.params.paged = 1;
                this.fetchArticles();
            });
        });
    }

    updateUrlParams() {
        const urlParams = new URLSearchParams();
        if (this.params.service) {
            urlParams.set('service', this.params.service);
        }
        if (this.params.technology) {
            urlParams.set('technology', this.params.technology);
        }
        if (this.params.format) {
            urlParams.set('format', this.params.format);
        }
        // urlParams.set('paged', this.params.paged);
        const newUrl = `${window.location.pathname.replace(/\/page\/\d+\//, '/')}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        history.pushState({}, '', newUrl);
    }

    getNextPage() {
        this.params.paged++;
        this.lastFetch = 'next';
        this.fetchArticles(); // Adjusted method name for fetching Articles
    }

    fetchArticles() { // Adjusted method name for fetching Articles

        if (this.request && this.request.abort) {
            try { this.request.abort(); } catch (e) { }
        }
        
        this.updateUrlParams();
        if (this.DOM.moreBtn) {
            this.DOM.moreBtn.classList.add('is-loading');
        }

        this.request = request.AJAX({
            'url': window.JWP.ajax_url + "?action=" + this.action,
            'data': this.params,
            'success': this.onSuccess.bind(this)
        });
    }
    resetFilters() {
        this.params.service = null;
        this.params.technology = null;
        this.params.format = null;
        this.DOM.selects.forEach(select => {
            select.value = select.options[0].value
        })
        this.params.paged = 1; // Réinitialiser la page à 1 après la réinitialisation des filtres
        this.fetchArticles(); // Recharger les événements après la réinitialisation des filtres
        this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 1, x: 0 }, {
            autoAlpha: 0, x: -5, onComplete: () => {
                this.app.gsap.set(this.DOM.resetBtn, { display: 'none' })
            }
        })
    }
    // helper animate card article
    animateNewCards(nodes, stagger = 0.06) {
        if (!nodes || !nodes.length) return;
        gsap.fromTo(nodes,
            { y: 40, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.75, ease: "power3.out", stagger }
        );
    }

    onSuccess(response) {
        const data = response.data;

        if (this.lastFetch === 'filter') this.clearColumn();

        if (data.articles && data.articles.length) {
            // inject
            data.articles.forEach(a => {
                this.DOM.column.insertAdjacentHTML('beforeend', a.html);
            });

            const cards = Array.from(this.DOM.column.querySelectorAll('.c-article-card'));
            const lastBatch = cards.slice(-data.articles.length);
            this._observeCards(lastBatch);

            this.DOM.moreBtn?.classList.remove('is-loading');
            setTimeout(() => this.app.$scroll?.update?.(), 100);
        }

        gsap.set(this.DOM.noPostMsg, { display: !data.articles?.length && this.lastFetch === "filter" ? 'block' : 'none' });
        if (this.DOM.moreBtn) {
            if (data.has_more) {
                gsap.set(this.DOM.moreBtn, { display: 'flex' });
                gsap.fromTo(this.DOM.moreBtn, { y: 4, autoAlpha: 0.9 }, { y: 0, autoAlpha: 1, duration: 0.3, ease: "power2.out" });
            } else {
                gsap.to(this.DOM.moreBtn, {
                    autoAlpha: 0, duration: 0.2, ease: "power2.out",
                    onComplete: () => gsap.set(this.DOM.moreBtn, { display: 'none', clearProps: 'opacity,transform' })
                });
            }
        }
    }


    clearColumn() {
        this.DOM.column.innerHTML = '';
    }

    destroy() {
        // Clean up event listeners or other resources
    }
}
